import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
    Accessibility,
    ArrowLeft,
    ArrowRight,
    CalendarDays,
    Clock,
    ExternalLink,
    Eye,
    Heart,
    Info,
    Loader2,
    Map as MapIcon,
    MapPin,
    MessageCircle,
    MousePointerClick,
    ParkingSquare,
    Share2,
    Ticket,
    User,
    Users,
    Utensils,
    Video,
    Volume2,
    Wifi,
} from "lucide-react";

import { getPublicEventById } from "../services/CreateurService";
import { base_url } from "../utils/constante";
import Seo from "../components/Seo";

// ======================================================
// LIENS STORES (billetterie Thrundrz = achat dans l'app)
// ======================================================

const PLAY_STORE = "https://play.google.com/store/apps/details?id=fr.thrundrz.even.thrundrz&pcampaignid=web_share";
const APP_STORE = "https://apps.apple.com/us/app/thrundrz/id6756219738";

// ======================================================
// LOADING
// ======================================================

const LoadingEvent = () => (
    <div
        className="min-h-screen bg-[#07060f] flex flex-col items-center justify-center gap-4"
        role="status"
        aria-live="polite"
    >
        <Loader2 className="w-12 h-12 animate-spin text-violet-500" aria-hidden="true" />
        <p className="text-white/60 text-base sm:text-lg">Chargement de l'événement…</p>
    </div>
);

// ======================================================
// ERROR
// ======================================================

const ErrorEvent = ({ message }) => (
    <div className="min-h-screen bg-[#07060f] flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center">
            <div className="text-6xl mb-6" aria-hidden="true">😕</div>
            <h1 className="text-2xl font-bold text-white mb-3">Événement introuvable</h1>
            <p className="text-white/60 mb-8">
                {message || "Cet événement n'existe pas ou n'est plus disponible."}
            </p>
            <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-violet-600 text-white
                           rounded-full font-semibold hover:bg-violet-700 transition
                           focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
                <ArrowLeft size={18} aria-hidden="true" />
                Retour à l'accueil
            </Link>
        </div>
    </div>
);

// ======================================================
// HELPERS DATE
// ======================================================

function toDate(value) {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(value) {
    const d = toDate(value);
    if (!d) return null;
    return new Intl.DateTimeFormat("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(d);
}

function formatDateCourte(value) {
    const d = toDate(value);
    if (!d) return null;
    return new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(d);
}

function formatTime(value) {
    const d = toDate(value);
    if (!d) return null;
    return new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(d);
}

function formatDateCommentaire(value) {
    const d = toDate(value);
    if (!d) return "";
    return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" }).format(d);
}

function formatNombre(n) {
    const v = Number(n) || 0;
    if (v >= 1000) return `${(v / 1000).toFixed(1).replace(".0", "")}K`;
    return String(v);
}

// ======================================================
// IMAGES
// ======================================================

const ORIGIN = (() => {
    try {
        return new URL(base_url).origin;
    } catch (_) {
        return "https://thrundrz.fr";
    }
})();

const IMAGE_DEFAUT = `${ORIGIN}/logo.png`;

/** imgEven = "url1#url2#url3" → [url1, url2, url3] */
function getImages(imgEven) {
    if (!imgEven || imgEven === "empty") return [IMAGE_DEFAUT];
    const images = imgEven
        .split("#")
        .map((i) => i.trim())
        .filter(Boolean);
    return images.length > 0 ? images : [IMAGE_DEFAUT];
}

// ======================================================
// TARIFS — [{"titre":"Adulte","prix":"20.45"}, ...]
// ======================================================

function parseTarifs(prixEven) {
    if (!prixEven) return [];
    const raw = String(prixEven).trim();
    if (raw === "" || raw === "0") return [];

    if (raw.startsWith("[")) {
        try {
            return JSON.parse(raw)
                .filter((t) => t && t.titre)
                .map((t) => ({ titre: t.titre, prix: parseFloat(t.prix) || 0 }));
        } catch (_) {
            return [];
        }
    }

    const val = parseFloat(raw);
    if (!isNaN(val) && val > 0) return [{ titre: "Tarif", prix: val }];
    return [];
}

function formatPrix(p) {
    return `${Number(p).toFixed(2).replace(".", ",")} €`;
}

// ======================================================
// DESCRIPTION POUR SEO
// ======================================================

function cleanDescription(description) {
    if (!description) return "";
    const div = document.createElement("div");
    div.innerHTML = description;
    return div.textContent.replace(/\s+/g, " ").trim();
}

// ======================================================
// ÉQUIPEMENTS
// ======================================================

const EQUIPEMENTS = [
    { cle: "isRestaurant", Icone: Utensils, label: "Restauration sur place" },
    { cle: "isParking", Icone: ParkingSquare, label: "Parking disponible" },
    { cle: "isAuditive", Icone: Volume2, label: "Boucle auditive" },
    { cle: "isHandicap", Icone: Accessibility, label: "Accessible PMR" },
    { cle: "isPersonnesSituationHandicapMental", Icone: User, label: "Accueil handicap mental" },
];

const estVrai = (v) => v === true || v === 1 || v === "1";

// ======================================================
// ONGLETS
// ======================================================

const ONGLETS = [
    { id: "a-propos", label: "À propos", Icone: Info },
    { id: "infos-pratiques", label: "Infos pratiques", Icone: MapIcon },
    { id: "avis", label: "Avis", Icone: MessageCircle },
];

// ======================================================
// PAGE
// ======================================================

const PublicEvent = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [event, setEvent] = useState(null);
    const [commentaires, setCommentaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [imageActive, setImageActive] = useState(0);
    const [ongletActif, setOngletActif] = useState("a-propos");
    const [lienCopie, setLienCopie] = useState(false);

    // ==================================================
    // FETCH
    // ==================================================

    useEffect(() => {
        const loadEvent = async () => {
            if (!id || Number.isNaN(Number(id))) {
                setError("Identifiant de l'événement invalide.");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const data = await getPublicEventById(Number(id));
                const evenement = data.evenement;
                if (!evenement) throw new Error("Événement introuvable");

                evenement.eventCreator = data?.users?.event_creator || null;

                setEvent(evenement);
                setCommentaires(Array.isArray(data.commentaires) ? data.commentaires : []);
                setImageActive(0);
            } catch (err) {
                console.error("Erreur chargement événement :", err);
                setError(err?.message || "Impossible de récupérer cet événement.");
            } finally {
                setLoading(false);
            }
        };

        loadEvent();
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [id]);

    // ==================================================
    // ONGLET ACTIF SELON LE SCROLL
    // ==================================================

    useEffect(() => {
        if (!event) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setOngletActif(entry.target.id);
                });
            },
            { rootMargin: "-45% 0px -50% 0px" }
        );

        ONGLETS.forEach(({ id: sectionId }) => {
            const el = document.getElementById(sectionId);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [event]);

    const allerA = (sectionId) => {
        setOngletActif(sectionId);
        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // ==================================================
    // RETOUR
    // ==================================================

    const handleRetour = () => {
        if (window.history.state && window.history.state.idx > 0) navigate(-1);
        else navigate("/");
    };

    // ==================================================
    // PARTAGE
    // ==================================================

    const handlePartage = async () => {
        const url = `https://thrundrz.fr/evenement/${id}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: event?.nomEven || "Thrundrz", url });
            } else {
                await navigator.clipboard.writeText(url);
                setLienCopie(true);
                setTimeout(() => setLienCopie(false), 2000);
            }
        } catch (_) {
            /* partage annulé */
        }
    };

    // ==================================================
    // DONNÉES CALCULÉES
    // ==================================================

    const images = useMemo(() => getImages(event?.imgEven), [event]);
    const image = images[0];
    const tarifs = useMemo(() => parseTarifs(event?.prixEven), [event]);

    // ==================================================
    // SEO
    // ==================================================

    const seo = useMemo(() => {
        if (!event) {
            return {
                title: "Événement | Thrundrz",
                description: "Découvrez les événements sur Thrundrz.",
                canonical: `https://thrundrz.fr/evenement/${id}`,
                image: IMAGE_DEFAUT,
                structuredData: null,
            };
        }

        const description = cleanDescription(event.description);
        const seoDescription = description
            ? description.substring(0, 155)
            : `Découvrez ${event.nomEven} sur Thrundrz.`;

        let seoTitle = event.nomEven || "Événement";
        if (event.villeEven) seoTitle += ` à ${event.villeEven}`;
        seoTitle += " | Thrundrz";

        const debut = toDate(event.dateEven);
        const prixStructure = estVrai(event.isGratuit)
            ? "0"
            : String(tarifs[0]?.prix ?? event.prix_billet ?? "0");

        const enLigne = event.typeEvent?.toLowerCase() === "en ligne";

        const structuredData = {
            "@context": "https://schema.org",
            "@type": "Event",
            name: event.nomEven,
            description: description || seoDescription,
            url: `https://thrundrz.fr/evenement/${event.id}`,
            image: images,
            startDate: debut ? debut.toISOString() : undefined,
            duration: event.duree || undefined,
            eventStatus: "https://schema.org/EventScheduled",
            eventAttendanceMode: enLigne
                ? "https://schema.org/OnlineEventAttendanceMode"
                : "https://schema.org/OfflineEventAttendanceMode",
            location: enLigne
                ? { "@type": "VirtualLocation", url: event.lien_reunion || event.site || undefined }
                : {
                    "@type": "Place",
                    name: event.villeEven
                        ? `${event.nomEven} — ${event.villeEven}`
                        : event.nomEven || "Lieu de l'événement",
                    address: {
                        "@type": "PostalAddress",
                        streetAddress: event.rueEven || undefined,
                        postalCode: event.codePostalEven || undefined,
                        addressLocality: event.villeEven || undefined,
                        addressRegion: "Bourgogne-Franche-Comté",
                        addressCountry: "FR",
                    },
                },
            offers: {
                "@type": "Offer",
                url: event.lien_billetterie || event.site || `https://thrundrz.fr/evenement/${event.id}`,
                price: prixStructure,
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                validFrom: new Date().toISOString(),
            },
        };

        if (event.eventCreator) {
            structuredData.organizer = {
                "@type": "Organization",
                name: event.eventCreator.name || "Organisateur",
                url: "https://thrundrz.fr",
            };
        }

        const breadcrumb = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Accueil", item: "https://thrundrz.fr/" },
                { "@type": "ListItem", position: 2, name: event.nomCat || "Événements", item: "https://thrundrz.fr/" },
                { "@type": "ListItem", position: 3, name: event.nomEven, item: `https://thrundrz.fr/evenement/${event.id}` },
            ],
        };

        return {
            title: seoTitle,
            description: seoDescription,
            canonical: `https://thrundrz.fr/evenement/${event.id}`,
            image,
            type: "article",
            structuredData: [structuredData, breadcrumb],
        };
    }, [event, id, image, images, tarifs]);

    // ==================================================
    // ÉTATS
    // ==================================================

    if (loading) return <LoadingEvent />;

    if (error || !event) {
        return (
            <>
                <Seo
                    title="Événement introuvable | Thrundrz"
                    description="Cet événement n'est pas disponible."
                    canonical={`https://thrundrz.fr/evenement/${id}`}
                    noindex
                />
                <ErrorEvent message={error} />
            </>
        );
    }

    // ==================================================
    // DATA
    // ==================================================

    const formattedDate = formatDate(event.dateEven);
    const dateCourte = formatDateCourte(event.dateEven);
    const formattedTime = formatTime(event.dateEven);

    const creator = event.eventCreator;
    const isEnLigne = event.typeEvent?.toLowerCase() === "en ligne";
    const isGratuit = estVrai(event.isGratuit);

    const address = isEnLigne
        ? ""
        : [event.rueEven, event.codePostalEven, event.villeEven].filter(Boolean).join(", ");

    const mapsQuery = encodeURIComponent(address);
    const mapsEmbed = `https://maps.google.com/maps?q=${mapsQuery}&z=15&output=embed`;
    const mapsLien = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;

    const equipements = isEnLigne ? [] : EQUIPEMENTS.filter((e) => estVrai(event[e.cle]));

    // Prix le plus bas pour la carte de réservation
    const prixMin = tarifs.length > 0
        ? Math.min(...tarifs.map((t) => t.prix))
        : parseFloat(event.prix_billet) || null;

    // Bouton de réservation :
    //  1. lien de billetterie externe
    //  2. billetterie Thrundrz → achat dans l'app
    //  3. site de l'événement
    const lienExterne = event.lien_billetterie || null;
    const billetterieThrundrz =
        !lienExterne &&
        event.billetterie_type &&
        !/aucun|none|externe/i.test(String(event.billetterie_type));
    const reservation = lienExterne
        ? { href: lienExterne, label: "Réserver mes billets" }
        : billetterieThrundrz
            ? { href: /iPhone|iPad|iPod/i.test(navigator.userAgent) ? APP_STORE : PLAY_STORE, label: "Réserver dans l'app" }
            : event.site
                ? { href: event.site, label: "En savoir plus" }
                : null;

    // Chips sous le titre
    const chips = [
        isEnLigne ? "En ligne" : "Présentiel",
        isGratuit ? "Gratuit" : null,
        event.villeEven || null,
    ].filter(Boolean);

    // Styles communs
    const carte = "rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-sm";
    const titreSection = "flex items-center gap-3 text-lg sm:text-2xl font-bold text-white";
    const barre = <span className="w-1 h-6 sm:h-7 rounded-full bg-[linear-gradient(to_bottom,#a78bfa,#d946ef)]" aria-hidden="true" />;

    // ==================================================
    // RENDER
    // ==================================================

    return (
        <>
            <Seo
                title={seo.title}
                description={seo.description}
                canonical={seo.canonical}
                image={seo.image}
                type={seo.type}
                structuredData={seo.structuredData}
            />

            <a
                href="#contenu-principal"
                className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4
                           focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-gray-900
                           focus:rounded-lg focus:shadow-lg"
            >
                Aller au contenu
            </a>

            <main className="min-h-screen bg-[#07060f] text-white overflow-x-hidden">

                {/* =====================================
                    HERO
                ====================================== */}

                <section className="relative w-full" aria-label="Présentation de l'événement">
                    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24 pb-6 sm:pb-10">
                        {/* Boutons haut */}
                        <div className="flex items-center justify-between mb-10 sm:mb-16">
                            <button
                                type="button"
                                onClick={handleRetour}
                                aria-label="Revenir à la page précédente"
                                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-black/40 border border-white/15 backdrop-blur
                                           flex items-center justify-center hover:bg-black/60 transition
                                           focus:outline-none focus:ring-2 focus:ring-violet-400"
                            >
                                <ArrowLeft size={20} aria-hidden="true" />
                            </button>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handlePartage}
                                    aria-label="Partager l'événement"
                                    className="relative w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-black/40 border border-white/15 backdrop-blur
                                               flex items-center justify-center hover:bg-black/60 transition
                                               focus:outline-none focus:ring-2 focus:ring-violet-400"
                                >
                                    <Share2 size={18} aria-hidden="true" />
                                    {lienCopie && (
                                        <span className="absolute top-12 right-0 whitespace-nowrap text-xs bg-violet-600 px-2 py-1 rounded-md">
                                            Lien copié
                                        </span>
                                    )}
                                </button>
                                <div
                                    className="h-10 sm:h-11 px-3 rounded-full bg-black/40 border border-white/15 backdrop-blur
                                               flex items-center gap-1.5 text-sm"
                                    aria-label={`${event.nbr_jaime || 0} j'aime`}
                                >
                                    <Heart size={18} className="text-fuchsia-400" aria-hidden="true" />
                                    <span>{formatNombre(event.nbr_jaime)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-10">
                            {/* Infos principales */}
                            <div className="flex-1 min-w-0">
                                {event.nomCat && (
                                    <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full
                                                     bg-gradient-to-r from-violet-600 to-fuchsia-500 text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                                        <Ticket size={14} aria-hidden="true" />
                                        {event.nomCat}
                                    </span>
                                )}

                                <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight mb-3 sm:mb-4 break-words">
                                    {event.nomEven}
                                </h1>

                                <div className="flex flex-col gap-2 text-sm sm:text-base text-white/85 mb-3 sm:mb-4">
                                    {dateCourte && (
                                        <p className="flex items-center gap-2">
                                            <CalendarDays size={18} className="shrink-0 text-violet-300" aria-hidden="true" />
                                            <span>
                                                {dateCourte}
                                                {formattedTime && ` · ${formattedTime}`}
                                            </span>
                                        </p>
                                    )}
                                    <p className="flex items-center gap-2">
                                        {isEnLigne ? (
                                            <Wifi size={18} className="shrink-0 text-violet-300" aria-hidden="true" />
                                        ) : (
                                            <MapPin size={18} className="shrink-0 text-violet-300" aria-hidden="true" />
                                        )}
                                        <span className="break-words">{isEnLigne ? "Événement en ligne" : address}</span>
                                    </p>
                                </div>

                                {event.sousNomEven && event.sousNomEven.trim().toLowerCase() !== (event.nomCat || "").trim().toLowerCase() && (
                                    <p className="text-sm sm:text-base text-white/75 mb-3 sm:mb-4">{event.sousNomEven}</p>
                                )}

                                <ul className="flex flex-wrap gap-2" aria-label="Caractéristiques">
                                    {chips.map((c) => (
                                        <li
                                            key={c}
                                            className="px-3 py-1 rounded-full bg-white/10 border border-white/10 text-xs sm:text-sm text-white/85"
                                        >
                                            {c}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Carte réservation */}
                            <div className={`${carte} w-full lg:w-80 p-4 sm:p-5 shrink-0`}>
                                <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                                    <div>
                                        <Eye size={18} className="mx-auto text-violet-300 mb-1" aria-hidden="true" />
                                        <p className="text-sm sm:text-base font-bold">{formatNombre(event.nbr_vues)}</p>
                                        <p className="text-[11px] sm:text-xs text-white/55">vues</p>
                                    </div>
                                    <div>
                                        <Heart size={18} className="mx-auto text-fuchsia-400 mb-1" aria-hidden="true" />
                                        <p className="text-sm sm:text-base font-bold">{formatNombre(event.nbr_jaime)}</p>
                                        <p className="text-[11px] sm:text-xs text-white/55">j'aime</p>
                                    </div>
                                    <div>
                                        <MessageCircle size={18} className="mx-auto text-violet-300 mb-1" aria-hidden="true" />
                                        <p className="text-sm sm:text-base font-bold">{commentaires.length}</p>
                                        <p className="text-[11px] sm:text-xs text-white/55">avis</p>
                                    </div>
                                </div>

                                {(isGratuit || prixMin) ? (
                                    <div className="flex items-baseline justify-between mb-4 pt-4 border-t border-white/10">
                                        <span className="text-xs sm:text-sm text-white/60">
                                            {isGratuit ? "Entrée" : tarifs.length > 1 ? "À partir de" : "Tarif"}
                                        </span>
                                        <span className="text-xl sm:text-2xl font-extrabold">
                                            {isGratuit
                                                ? <span className="text-emerald-400">Gratuit</span>
                                                : formatPrix(prixMin)}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="mb-4 border-t border-white/10" />
                                )}

                                {isEnLigne && event.lien_reunion && (
                                    <a
                                        href={event.lien_reunion}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mb-2.5 flex items-center justify-center gap-2 w-full rounded-full
                                                   bg-blue-600 hover:bg-blue-700 px-5 py-3 text-sm sm:text-base font-semibold transition
                                                   focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    >
                                        <Video size={18} aria-hidden="true" />
                                        Rejoindre la réunion
                                        <span className="sr-only"> (ouvre un nouvel onglet)</span>
                                    </a>
                                )}

                                {reservation && (
                                    <a
                                        href={reservation.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-2 w-full rounded-full
                                                   bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:brightness-110
                                                   px-5 py-3 text-sm sm:text-base font-semibold shadow-lg shadow-violet-900/40 transition
                                                   focus:outline-none focus:ring-2 focus:ring-violet-400"
                                    >
                                        {reservation.label}
                                        <ArrowRight size={18} aria-hidden="true" />
                                        <span className="sr-only"> (ouvre un nouvel onglet)</span>
                                    </a>
                                )}

                                {event.nbr_places_max && Number(event.nbr_places_max) > 0 && (
                                    <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-white/55">
                                        <Users size={14} aria-hidden="true" />
                                        {event.nbr_places_max} places au total
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* =====================================
                    ONGLETS
                ====================================== */}

                <nav
                    aria-label="Sections de l'événement"
                    className="sticky top-16 sm:top-20 z-20 bg-[#07060f]/90 backdrop-blur border-y border-white/5"
                >
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
                        <ul className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                            {ONGLETS.map(({ id: sectionId, label, Icone }) => {
                                const actif = ongletActif === sectionId;
                                return (
                                    <li key={sectionId} className="shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => allerA(sectionId)}
                                            aria-current={actif ? "true" : undefined}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition
                                                        focus:outline-none focus:ring-2 focus:ring-violet-400 ${actif
                                                    ? "bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white shadow-lg shadow-violet-900/40"
                                                    : "text-white/70 hover:text-white hover:bg-white/5"
                                                }`}
                                        >
                                            <Icone size={16} aria-hidden="true" />
                                            {label}
                                            {sectionId === "avis" && commentaires.length > 0 && (
                                                <span className="text-xs opacity-80">({commentaires.length})</span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </nav>

                {/* =====================================
                    CONTENU
                ====================================== */}

                <div
                    id="contenu-principal"
                    className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 flex flex-col gap-6 sm:gap-8"
                >
                    {/* ─────────── À PROPOS + GALERIE ─────────── */}
                    <section id="a-propos" className="scroll-mt-40 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                        <div className="min-w-0 flex flex-col gap-5">
                            <h2 className={titreSection}>{barre} À propos de l'événement</h2>

                            {event.description ? (
                                <div
                                    className="prose prose-invert prose-sm sm:prose-base max-w-none min-w-0 text-white/80
                                               break-words [overflow-wrap:anywhere] [&_*]:break-words [&_a]:text-violet-300 [&_a]:break-all"
                                    dangerouslySetInnerHTML={{ __html: event.description }}
                                />
                            ) : (
                                <p className="text-sm text-white/50">Aucune description pour le moment.</p>
                            )}

                            {creator && (
                                <div className={`${carte} flex items-center gap-4 p-4 border-violet-500/30 bg-violet-600/10`}>
                                    {creator.photo ? (
                                        <img
                                            src={creator.photo}
                                            alt=""
                                            width={52}
                                            height={52}
                                            loading="lazy"
                                            className="w-12 h-12 rounded-full object-cover ring-2 ring-violet-500/60"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-violet-600/30 flex items-center justify-center" aria-hidden="true">
                                            <User size={22} />
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <p className="text-xs text-violet-300 font-medium">Organisé par</p>
                                        <p className="font-semibold text-sm sm:text-base truncate">
                                            {creator.name || "Organisateur"}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {event.ref && <p className="text-xs text-white/35">Réf : {event.ref}</p>}
                        </div>

                        {/* Galerie */}
                        <div className="min-w-0 flex flex-col gap-3">
                            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black">
                                <img
                                    src={images[imageActive]}
                                    alt={`Photo ${imageActive + 1} de l'événement ${event.nomEven}`}
                                    className="w-full h-auto max-h-[75vh] object-contain mx-auto"
                                    onError={(e) => { e.currentTarget.src = IMAGE_DEFAUT; }}
                                />
                                {images.length > 1 && (
                                    <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-xs font-semibold">
                                        {imageActive + 1}/{images.length}
                                    </span>
                                )}
                            </div>

                            {images.length > 1 && (
                                <div className="grid grid-cols-4 gap-2 sm:gap-3">
                                    {images.slice(0, 8).map((src, i) => (
                                        <button
                                            key={src + i}
                                            type="button"
                                            onClick={() => setImageActive(i)}
                                            aria-label={`Afficher la photo ${i + 1}`}
                                            className={`rounded-lg sm:rounded-xl overflow-hidden border-2 transition
                                                        focus:outline-none focus:ring-2 focus:ring-violet-400 ${i === imageActive
                                                    ? "border-violet-500"
                                                    : "border-transparent opacity-70 hover:opacity-100"
                                                }`}
                                        >
                                            <img src={src} alt="" loading="lazy" className="w-full h-14 sm:h-20 object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ─────────── BARRE ÉQUIPEMENTS ─────────── */}
                    {equipements.length > 0 && (
                        <section aria-label="Services et accessibilité" className={`${carte} px-4 py-3 sm:px-6 sm:py-4`}>
                            <ul className="flex flex-wrap gap-x-6 gap-y-3 justify-center sm:justify-between">
                                {equipements.map(({ cle, Icone, label }) => (
                                    <li key={cle} className="flex items-center gap-2 text-xs sm:text-sm text-white/80">
                                        <Icone size={20} className="text-violet-300 shrink-0" aria-hidden="true" />
                                        {label}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* ─────────── LIEU + INFOS PRATIQUES ─────────── */}
                    <section id="infos-pratiques" className="scroll-mt-40 grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">
                        {/* Carte */}
                        {!isEnLigne && address && (
                            <div className={`${carte} lg:col-span-3 p-4 sm:p-5 flex flex-col gap-4`}>
                                <h2 className="flex items-center gap-3 text-base sm:text-lg font-bold">{barre} Lieu de l'événement</h2>
                                <div className="relative rounded-xl overflow-hidden border border-white/10 h-56 sm:h-72">
                                    <iframe
                                        title={`Carte : ${address}`}
                                        src={mapsEmbed}
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        className="w-full h-full border-0"
                                        style={{ filter: "invert(90%) hue-rotate(180deg) saturate(0.7) brightness(0.95)" }}
                                    />
                                    <a
                                        href={mapsLien}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="absolute bottom-3 left-3 inline-flex items-center gap-2 px-3 py-2 rounded-full
                                                   bg-[#07060f]/85 backdrop-blur border border-white/15 text-xs sm:text-sm font-medium
                                                   hover:bg-[#07060f] transition focus:outline-none focus:ring-2 focus:ring-violet-400"
                                    >
                                        <MapIcon size={16} aria-hidden="true" />
                                        Voir sur Google Maps
                                        <span className="sr-only"> (ouvre un nouvel onglet)</span>
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* Infos pratiques */}
                        <div className={`${carte} ${!isEnLigne && address ? "lg:col-span-2" : "lg:col-span-5"} p-4 sm:p-5`}>
                            <h2 className="flex items-center gap-3 text-base sm:text-lg font-bold mb-4">
                                {barre} Informations pratiques
                            </h2>

                            <dl className="flex flex-col gap-4 m-0">
                                {formattedDate && (
                                    <div className="flex gap-3">
                                        <CalendarDays size={20} className="text-violet-300 shrink-0 mt-0.5" aria-hidden="true" />
                                        <div className="min-w-0">
                                            <dt className="text-sm font-semibold">Date</dt>
                                            <dd className="text-sm text-white/70 capitalize m-0">{formattedDate}</dd>
                                        </div>
                                    </div>
                                )}

                                {(formattedTime || event.duree) && (
                                    <div className="flex gap-3">
                                        <Clock size={20} className="text-violet-300 shrink-0 mt-0.5" aria-hidden="true" />
                                        <div className="min-w-0">
                                            <dt className="text-sm font-semibold">Horaires</dt>
                                            <dd className="text-sm text-white/70 m-0">
                                                {formattedTime}
                                                {event.duree && `${formattedTime ? " · " : ""}Durée : ${event.duree}`}
                                            </dd>
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-3">
                                    {isEnLigne ? (
                                        <Wifi size={20} className="text-violet-300 shrink-0 mt-0.5" aria-hidden="true" />
                                    ) : (
                                        <MapPin size={20} className="text-violet-300 shrink-0 mt-0.5" aria-hidden="true" />
                                    )}
                                    <div className="min-w-0">
                                        <dt className="text-sm font-semibold">Lieu</dt>
                                        <dd className="text-sm text-white/70 m-0">
                                            {isEnLigne ? (
                                                "Événement en ligne"
                                            ) : (
                                                <address className="not-italic break-words">{address || "Non renseigné"}</address>
                                            )}
                                        </dd>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <Ticket size={20} className="text-violet-300 shrink-0 mt-0.5" aria-hidden="true" />
                                    <div className="min-w-0 flex-1">
                                        <dt className="text-sm font-semibold">Tarifs</dt>
                                        <dd className="text-sm text-white/70 m-0">
                                            {isGratuit ? (
                                                <span className="text-emerald-400 font-semibold">Gratuit</span>
                                            ) : tarifs.length > 0 ? (
                                                <ul className="mt-1 flex flex-col gap-1">
                                                    {tarifs.map((t, i) => (
                                                        <li key={i} className="flex justify-between gap-3">
                                                            <span className="truncate">{t.titre}</span>
                                                            <span className="font-semibold text-white shrink-0">{formatPrix(t.prix)}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : prixMin ? (
                                                formatPrix(prixMin)
                                            ) : (
                                                "Non renseigné"
                                            )}
                                        </dd>
                                    </div>
                                </div>

                                {event.nbr_places_max && Number(event.nbr_places_max) > 0 && (
                                    <div className="flex gap-3">
                                        <Users size={20} className="text-violet-300 shrink-0 mt-0.5" aria-hidden="true" />
                                        <div className="min-w-0">
                                            <dt className="text-sm font-semibold">Capacité</dt>
                                            <dd className="text-sm text-white/70 m-0">{event.nbr_places_max} places</dd>
                                        </div>
                                    </div>
                                )}
                            </dl>

                            {event.site && (
                                <a
                                    href={event.site}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-sm
                                               text-violet-300 hover:text-violet-200 transition focus:outline-none focus:underline"
                                >
                                    Plus d'infos sur le site officiel
                                    <ExternalLink size={16} aria-hidden="true" />
                                    <span className="sr-only"> (ouvre un nouvel onglet)</span>
                                </a>
                            )}
                        </div>
                    </section>

                    {/* ─────────── AVIS / COMMENTAIRES ─────────── */}
                    <section id="avis" className="scroll-mt-40 flex flex-col gap-4">
                        <div className="flex items-center justify-between gap-3">
                            <h2 className={titreSection}>
                                {barre} Avis de la communauté
                                <span className="text-sm font-medium text-white/50">({commentaires.length})</span>
                            </h2>
                        </div>

                        {commentaires.length === 0 ? (
                            <div className={`${carte} p-5 sm:p-6 text-center`}>
                                <MessageCircle size={28} className="mx-auto text-violet-300 mb-2" aria-hidden="true" />
                                <p className="text-sm text-white/70">Aucun avis pour le moment.</p>
                                <p className="text-xs text-white/45 mt-1">
                                    Donnez le vôtre depuis l'application Thrundrz.
                                </p>
                            </div>
                        ) : (
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                                {commentaires.map((c) => (
                                    <li key={c.id} className={`${carte} p-4 flex gap-3`}>
                                        {c.comment_creator_photo ? (
                                            <img
                                                src={c.comment_creator_photo}
                                                alt=""
                                                loading="lazy"
                                                className="w-10 h-10 rounded-full object-cover shrink-0"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-violet-600/30 flex items-center justify-center shrink-0" aria-hidden="true">
                                                <User size={18} />
                                            </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-baseline justify-between gap-2">
                                                <p className="text-sm font-semibold truncate">{c.comment_creator || "Membre"}</p>
                                                <time dateTime={c.date_comment} className="text-[11px] text-white/45 shrink-0">
                                                    {formatDateCommentaire(c.date_comment)}
                                                </time>
                                            </div>
                                            <p className="text-sm text-white/75 mt-1 break-words [overflow-wrap:anywhere]">
                                                {c.content}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* ─────────── STATS + RETOUR ─────────── */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-white/10">
                        <div className="flex items-center gap-4 text-xs sm:text-sm text-white/50">
                            <span className="flex items-center gap-1.5"><Eye size={15} aria-hidden="true" /> {event.nbr_vues || 0} vues</span>
                            <span className="flex items-center gap-1.5"><MousePointerClick size={15} aria-hidden="true" /> {event.nbr_clicks || 0} clics</span>
                        </div>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-sm text-violet-300 font-semibold hover:text-violet-200
                                       focus:outline-none focus:underline"
                        >
                            <ArrowLeft size={16} aria-hidden="true" />
                            Voir tous les événements
                        </Link>
                    </div>
                </div>

                {/* =====================================
                    BARRE RÉSERVATION FIXE — téléphone
                ====================================== */}
                {reservation && (
                    <div className="lg:hidden sticky bottom-0 z-30 bg-[#07060f]/95 backdrop-blur border-t border-white/10 px-4 py-3">
                        <div className="max-w-6xl mx-auto flex items-center gap-3">
                            <div className="min-w-0">
                                <p className="text-[11px] text-white/55">
                                    {isGratuit ? "Entrée" : tarifs.length > 1 ? "À partir de" : "Tarif"}
                                </p>
                                <p className="text-base font-extrabold leading-tight">
                                    {isGratuit ? "Gratuit" : prixMin ? formatPrix(prixMin) : "—"}
                                </p>
                            </div>
                            <a
                                href={reservation.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-auto flex items-center justify-center gap-2 rounded-full
                                           bg-gradient-to-r from-violet-600 to-fuchsia-500 px-5 py-2.5 text-sm font-semibold
                                           focus:outline-none focus:ring-2 focus:ring-violet-400"
                            >
                                {reservation.label}
                                <ArrowRight size={16} aria-hidden="true" />
                            </a>
                        </div>
                    </div>
                )}
            </main>
        </>
    );
};

export default PublicEvent;